import { canonical } from "./seo";

/**
 * Default OG share card path served from /public/og/.
 * 1200x630 navy cards with the Durafoam logo — sized for Google,
 * Facebook, LinkedIn, X, iMessage, Slack, etc.
 */
export const DEFAULT_OG_IMAGE = "/og/og-home.png";
export const BLOG_DEFAULT_OG_IMAGE = "/og/og-blog.png";

/**
 * Slugs that have a dedicated /public/og/og-<slug>.png card.
 * Anything not in this set falls back to the default home card so
 * crawlers never get a 404 on og:image.
 */
const OG_CARDS = new Set<string>([
  "home",
  "about-us",
  "contact-us",
  "reviews",
  "free-roof-inspection-phoenix",
  "foam-roofing",
  "roof-repair",
  "foam-roof-coating-recoating",
  "roof-leak-repair",
  "skylight-repair",
  "roof-maintenance",
  "foam-roof-replacement-cost-phoenix",
  "roof-repair-cost-phoenix",
  "foam-roof-recoating-cost-in-phoenix",
  "skylight-repair-cost-phoenix",
  "blog",
]);

export function ogImageUrl(pathOrSlug?: string | null): string {
  if (!pathOrSlug) return canonical(DEFAULT_OG_IMAGE);
  // Full URL passed through (e.g. blog post's og_image_url field).
  if (/^https?:\/\//i.test(pathOrSlug)) return pathOrSlug;
  // Absolute path (e.g. "/og/og-foo.png") — use as-is.
  if (pathOrSlug.startsWith("/")) return canonical(pathOrSlug);
  // Slug lookup; fall back to the home card if we don't have a dedicated one.
  const slug = OG_CARDS.has(pathOrSlug) ? pathOrSlug : "home";
  return canonical(`/og/og-${slug}.png`);
}

/**
 * Returns a flat array of <meta> tags for og:image + twitter:image,
 * ready to spread into a route's head().meta array.
 */
export function ogImageMeta(pathOrSlug?: string | null) {
  const url = ogImageUrl(pathOrSlug);
  return [
    { property: "og:image", content: url },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: url },
  ];
}
